import React from 'react';
import { X, ShoppingBag, Building2, Palette, TrendingUp, ShieldCheck, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const LoginModal = ({ isOpen, onClose, defaultRole = 'consumer' }) => {
  const { login } = useAuth();

  if (!isOpen) return null;

  const loginTypes = [
    {
      id: 'consumer',
      title: 'Consumer Login',
      description: 'Shop for products at transparent prices',
      icon: ShoppingBag,
      showSocialLogin: true
    },
    {
      id: 'institutional',
      title: 'Institutional Customer',
      description: 'Company account for bulk orders',
      icon: Building2,
      showSocialLogin: false
    },
    {
      id: 'designer',
      title: 'Product Designer',
      description: 'Submit designs and earn royalties',
      icon: Palette,
      showSocialLogin: false
    },
    {
      id: 'investor',
      title: 'Micro Investor',
      description: 'Invest in inventory and earn returns',
      icon: TrendingUp,
      showSocialLogin: false
    },
    {
      id: 'admin',
      title: 'Admin Access',
      description: 'Platform management and oversight',
      icon: ShieldCheck,
      showSocialLogin: false
    }
  ];

  const handleLoginClick = (type) => {
    if (type.id === 'consumer') {
      // Show social login options for consumers
      return;
    } else if (type.id === 'admin') {
      // Redirect to admin login page
      window.location.href = '/admin/login';
    } else {
      // Redirect to registration form for other types
      window.location.href = `/register/${type.id}`;
    }
  };

  const handleSocialLogin = (provider) => {
    // Trigger Emergent Auth which supports social logins
    login();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col my-auto">
        {/* Header */}
        <div className="bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Select Login Type</h2>
            <p className="text-gray-600 text-xs sm:text-sm mt-1">Choose your account type to continue</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
          </button>
        </div>

        {/* Login Options - Scrollable */}
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 overflow-y-auto">
          {loginTypes.map((type) => {
            const Icon = type.icon;
            return (
              <div key={type.id} className="border-2 border-gray-200 rounded-xl p-4 sm:p-6 bg-white">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-subtle-stripes rounded-xl flex items-center justify-center mb-3 sm:mb-4 shadow-md">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{type.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3">{type.description}</p>
                
                {type.showSocialLogin ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleSocialLogin('google')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span className="text-sm font-medium">Continue with Google</span>
                    </button>
                    
                    <button
                      onClick={() => handleSocialLogin('facebook')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="h-5 w-5" fill="white" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <span className="text-sm font-medium">Continue with Facebook</span>
                    </button>
                    
                    <button
                      onClick={() => handleSocialLogin('email')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                    >
                      <Mail className="h-5 w-5" />
                      <span className="text-sm font-medium">Continue with Email</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleLoginClick(type)}
                    className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Register / Login
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 flex-shrink-0">
          <p className="text-xs sm:text-sm text-gray-600 text-center">
            Don't have an account?{' '}
            <a href="/role-selection" className="text-red-600 font-semibold hover:underline">
              Register as Partner
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
